import React, { useEffect, useState } from 'react';
import { dataForDayOffSupervisorTable, sortingDate } from 'shared/helpers/utils';
import { Badge, notification, Popconfirm, Skeleton, Spin } from 'antd';
import { useAppSelector } from 'app/store';
import { RequestsSearch } from 'shared/types/types';
import { usePullSort } from 'shared/hooks';
import { RequestStatusType, SortCondition } from 'shared/enums';
import { SearchKeysNews } from 'shared/enums/news';
import { getCurrentDivision } from 'entities/divisions/model';
import { TabKeys } from 'features/divisions';
import { ColumnsType } from 'antd/lib/table';
import { DayOffsStatusesMap, DayOffsStatusTypesMap } from 'shared/types/dayoff.types';
import { VacationModal } from 'features/vacations/vacationModal';
import { dayoffsSupervisorApi, TPerparedDayOffSupervisor } from 'app/services/edpApiV1/supervisor/dayoffSupervisor';
import { getDayOffSupervisor } from 'entities/supervisor/dayOff';
import { TableDayOff } from 'features/tables/supervisor/TableDayOff';
import { LoadingOutlined } from '@ant-design/icons';
import { returnErrorOrMessage } from 'shared/helpers/errors';
import style from './index.module.scss';

export type TPreparedVacationSupervisorInfoData = {
  key: number;
  id: number;
  fio: string;
  vacationType: string;
  type: string;
  startedAt: string;
  endedAt: string;
  amountDays: number;
  status: {
    name: string;
    type: RequestStatusType;
  };
};

export const VacationPull = () => {
  const [page, setCurrentPage] = useState<number>(1);
  const [size, setCurrentSize] = useState<number>(10);
  const [openModal, setOpenModal] = useState(false);
  const [sortInfo, sortTrigger] = usePullSort();
  const sortOption = sortInfo.value && sortInfo.value === SortCondition.ASCEND ? SortCondition.ASC : SortCondition.DESC;
  const [search, setSearch] = useState<RequestsSearch>({ byId: undefined, fio: undefined });
  const [requestToReject, setRequestToReject] = useState<number>(0);
  const [currentActionDayOff, setCurrentActionDayOff] = useState<number[]>([]);

  const { currentOption } = useAppSelector(getCurrentDivision);

  const [getDayOffsDeputy] = dayoffsSupervisorApi.useLazyDayoffsDeputyQuery();
  const [getDayOffsActing] = dayoffsSupervisorApi.useLazyDayoffsActingQuery();
  const [getDayOffsSupervisor] = dayoffsSupervisorApi.useLazyDayoffsSupervisorQuery();

  const [approveDayOffsDeputy, { isLoading: isDeputyApproveLoading }] =
    dayoffsSupervisorApi.useApproveDayoffDeputyMutation();
  const [approveDayOffsActing, { isLoading: isActingApproveLoading }] =
    dayoffsSupervisorApi.useApproveDayoffActingMutation();
  const [approveDayOffsSupervisor, { isLoading: isSupervisorApproveLoading }] =
    dayoffsSupervisorApi.useApproveDayoffSupervisorMutation();

  const [rejectDayOffsDeputy, { isLoading: isDeputyRejectLoading }] =
    dayoffsSupervisorApi.useRejectDayoffDeputyMutation();
  const [rejectDayOffsActing, { isLoading: isActingRejectLoading }] =
    dayoffsSupervisorApi.useRejectDayoffActingMutation();
  const [rejectDayOffsSupervisor, { isLoading: isSupervisorRejectLoading }] =
    dayoffsSupervisorApi.useRejectDayoffSupervisorMutation();

  const { data, pagination, loading, success, error } = useAppSelector(getDayOffSupervisor);

  const refetch = () => {
    if (!currentOption) {
      return;
    }

    try {
      switch (currentOption.type) {
        case TabKeys.ACTING:
          getDayOffsActing({ isArchive: false, page, size, divisionId: currentOption.value, ...search }).unwrap();
          break;
        case TabKeys.DEPUTY:
          getDayOffsDeputy({ isArchive: false, page, size, divisionId: currentOption.value, ...search }).unwrap();
          break;
        case TabKeys.SUPERVISOR:
          getDayOffsSupervisor({ isArchive: false, page, size, ...search }).unwrap();
          break;
      }
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
    }
  };

  useEffect(() => {
    refetch();
  }, [currentOption, search, page, size]);

  const approve = async (requestId: number) => {
    setCurrentActionDayOff([...currentActionDayOff, requestId]);

    if (!currentOption) {
      return;
    }
    const dayOff = data.find(elem => elem.request.id === requestId);
    const employeeId = dayOff?.employee?.id;

    if (!employeeId) {
      return;
    }
    try {
      switch (currentOption.type) {
        case TabKeys.ACTING:
          await approveDayOffsActing({ divisionId: currentOption.value, requestId, employeeId }).unwrap();
          break;
        case TabKeys.DEPUTY:
          await approveDayOffsDeputy({ requestId, employeeId, divisionId: currentOption.value }).unwrap();
          break;
        case TabKeys.SUPERVISOR:
          await approveDayOffsSupervisor({ requestId, employeeId }).unwrap();
          break;
      }
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
    }
    setCurrentActionDayOff(currentActionDayOff.filter(elem => elem === requestId));
  };

  const reject = (requestId: number) => {
    setOpenModal(true);
    setRequestToReject(requestId);
  };

  const handleSubmitRejectModal = async (rejectionReason: string) => {
    if (!currentOption) {
      return;
    }

    setCurrentActionDayOff([...currentActionDayOff, requestToReject]);

    const dayOff = data.find(elem => elem.request.id === requestToReject);
    const employeeId = dayOff?.employee?.id;

    if (!employeeId) {
      return;
    }
    try {
      switch (currentOption.type) {
        case TabKeys.ACTING:
          await rejectDayOffsActing({
            divisionId: currentOption.value,
            requestId: requestToReject,
            rejectionReason,
            employeeId,
          }).unwrap();
          break;
        case TabKeys.DEPUTY:
          await rejectDayOffsDeputy({
            requestId: requestToReject,
            rejectionReason,
            employeeId,
            divisionId: currentOption.value,
          }).unwrap();
          break;
        case TabKeys.SUPERVISOR:
          await rejectDayOffsSupervisor({ requestId: requestToReject, rejectionReason, employeeId }).unwrap();
          break;
      }
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
    }
    setCurrentActionDayOff(currentActionDayOff.filter(elem => elem === requestToReject));
  };

  const columns: ColumnsType<TPerparedDayOffSupervisor> = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },

    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },

    {
      title: 'Дата начала',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: 'Дата окончания',
      dataIndex: 'endDate',
      key: 'date',
    },

    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <span>
          <Badge className={style.badge} status={status ? DayOffsStatusTypesMap[status] : 'processing'} />
          {DayOffsStatusesMap[status]}
        </span>
      ),
    },
    {
      title: 'Действие',
      dataIndex: 'id',
      key: 'action',
      render: (id, data) => {
        return (
          <>
            {data.status === RequestStatusType.consideredBySupervisor && (
              <>
                {currentActionDayOff.includes(Number(id)) &&
                (isDeputyApproveLoading ||
                  isActingApproveLoading ||
                  isSupervisorApproveLoading ||
                  isDeputyRejectLoading ||
                  isActingRejectLoading ||
                  isSupervisorRejectLoading) ? (
                  <Spin indicator={<LoadingOutlined spin />} />
                ) : (
                  <>
                    <Popconfirm placement="leftTop" title="Вы уверены?" onConfirm={() => approve(id)}>
                      <a>Согласовать</a>
                    </Popconfirm>
                    <br />
                    <Popconfirm placement="leftTop" title="Вы уверены?" onConfirm={() => reject(id)}>
                      <a className={style.reject}>Отклонить</a>
                    </Popconfirm>
                    <VacationModal
                      id="1"
                      openModal={openModal}
                      setOpenModal={setOpenModal}
                      handleSubmit={handleSubmitRejectModal}
                    />
                  </>
                )}
              </>
            )}
          </>
        );
      },
    },
  ];

  const onSearch = (data: string, key: SearchKeysNews) => {
    if (key === SearchKeysNews.SEARCH_ID) {
      setSearch({ fio: undefined, byId: data ? data : undefined });
    } else if (key === SearchKeysNews.SEARCH_NAME) {
      setSearch({ byId: undefined, fio: data ? data : undefined });
    } else {
      setSearch({ [key]: data ? data : undefined });
    }
  };

  // useNotifications(successTakeToWork, errorTakeToWork, 'Взято в работу', 'Что-то пошло не так', resetTakeToWork);
  // useIndicators(isSuccess, isFetching, false);

  const onRowClick = (data: any) => {};

  return (
    <>
      {data && columns && (
        <TableDayOff
          dataSource={dataForDayOffSupervisorTable(data || [])}
          columns={columns}
          currentPage={page}
          setCurrentPage={setCurrentPage}
          currentSize={size}
          setCurrentSize={setCurrentSize}
          total={pagination?.total}
          onSearch={onSearch}
          onRowClick={onRowClick}
          loading={loading}
        />
      )}
    </>
  );
};
